var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { AssemblyServicesDataSourceEnum, NotificationLinkTypeEnum, SaveDataSourceEnum, StoreAvailabilityDataSourceEnum, ZipAvailabilityDataSourceEnum } from '@inter-ikea-kompis/enums';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { AccountService, AssemblyServicesService, CheckoutAvailabilityService, FinancingService, KompisDefaultFields, NotificationService, ProductService, SaveService, SettingsService, ShoppingCartService, ShoppingListService, StoreIdCookieInformationService, StoreService, TranslationsService, VpcService, ZipCodeCookieInformationService, ZipValidationService } from '@inter-ikea-kompis/services';
import { ProductUtility, ReducerUtility } from '@inter-ikea-kompis/utilities';
import { AddToBagStateEnum } from '@inter-ikea-kompis/component-add-to-bag';
import { ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum, ConfigurationSummaryToastMessage, ConfirmationSummaryKioskMainCTAButtonStateEnum, ConfirmationSummaryShareDesignStateEnum } from '@inter-ikea-kompis/component-configuration-summary';
import { ProductRowLayoutEnum, ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import { SaveDesignToProfileStateEnum, SaveDesignToProfileToastMessage } from '@inter-ikea-kompis/component-save-design-to-profile';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';
import { ToastManager } from '@inter-ikea-kompis/component-toast';
import { ZipInActionTypeEnum, ZipInDefaultState, zipInReducer, ZipInStepEnum, ZipInToastMessage } from '@inter-ikea-kompis/component-zip-in';
import SummaryPageConfigurationMeasureEnum from '../../enums/SummaryPageConfigurationMeasureEnum.js';
import SummaryPageConfirmationCardTypeEnum from '../../enums/SummaryPageConfirmationCardTypeEnum.js';
import SummaryPageControllerLoginBehaviourEnum from '../../enums/SummaryPageControllerLoginBehaviourEnum.js';
import SummaryPageModalEnum from '../../enums/SummaryPageModalEnum.js';
import SummaryPageCustomTabsUtility from '../../utilities/SummaryPageCustomTabsUtility.js';
import SummaryPage from '../summary-page/SummaryPage.js';
import SummaryPageControllerDefaultState from './SummaryPageControllerDefaultState.js';
/**
 * This component collects the most essential parts of a typical Summary view and provides them in a responsive layout. Includes product list, images, price display, add to bag, save design, screenshot and more.
 *
 * @event stateChange Fired when the state of the component changes.
 * @event kioskMainCtaButtonClick Fired when the main CTA button is clicked on a kiosk.
 * @event productListRemove Fired when a product is removed from the product list to be able to sync removed items with the planner. However, it is optional to listen to this event as the list will be updated internally.
 * @event logInAndSaveDesign Fired after the user clicks the login / sign-up button. SummaryPageController will immediately proceed with the redirect.
 * @event designSavedToProfile Fired when design has been saved to profile. The event details contains the id of the saved design.
 * @event linkCardClick Fired when the link card is clicked.
 * @event trackAnalytics Fired for each event that should be tracked when implementing a custom analytics solution. The event detail contains the event name and the event data.
 * @event printButtonClick Fired when the print button is clicked.
 * @event editDesignButtonClick Fired when the edit design button is clicked.
 * @event tabChange Fired when the selected tab changes.
 * @event printQrCodeRendered Triggered when the print QR code has rendered.
 *
 * @slot custom-print-content - Slot for custom content to be included in the print layout.
 * @slot custom-tab-1 - Slot for custom tab content.
 * @slot custom-tab-2 - Slot for custom tab content.
 * @slot custom-tab-3 - Slot for custom tab content.
 * @slot custom-tab-4 - Slot for custom tab content.
 * @slot custom-tab-5 - Slot for custom tab content.
 *
 * @tracker packages/component-summary-page/src/trackers/SummaryPageTracker.ts
 */
export default class SummaryPageController extends KompisElement {
    constructor() {
        super(...arguments);
        // Design configuration containing data about the created design that should be stored in the VPC service or by using "save to profile".
        this.designConfiguration = null;
        // Login behaviour for the save design to profile card.
        this.loginBehaviour = SummaryPageControllerLoginBehaviourEnum.redirect;
        // Theme.
        this.theme = SkapaTheme;
        // Controls how measurements should be shown in the Product list tab
        this.productMeasures = ProductRowProductMeasureEnum.none;
        // Controls how measurements should be shown in the configuration summary.
        this.configurationMeasures = SummaryPageConfigurationMeasureEnum.short;
        // The url for the screenshot.
        this.screenshotUrl = null;
        // The alt text for the screenshot. If not set, a default alt text will be used.
        this.screenshotAlt = null;
        // The name of the product, e.g. PLATSA
        this.productName = null;
        // Description of the product, e.g. "Wardrobe, 200x300x55 cm". Used in SaveDesignToProfile and ShareDesignCard.
        this.productDescription = null;
        // Shows the save design button.
        this.showSaveDesign = false;
        // VPC code for the solution (this or "designConfiguration" must be set to use VPC features)
        this.vpcCode = null;
        // Whether the current design is saved to the user's profile. If this is true, the design will not auto-save. This has no effect if the save to profile feature is not active.
        this.designSaved = false;
        // Set to "true" if kiosk.
        this.kiosk = false;
        // A list of images used in the "Images" tab
        this.images = null;
        // An object with information for the security tab
        this.securityInformation = null;
        // Measurements of height, width and depth in millimeter. Can be left empty to hide dimensions.
        this.measurements = null;
        // Whether to show the collect checkbox in the product list or not.
        this.collectableProducts = false;
        // Show text describing the color and/or material of the product.
        this.showDesignText = false;
        // A list of sections used in the product list.
        this.productListSections = null;
        // Defines optional custom tab buttons. Up to five tabs are supported.
        this.customTabButtons = null;
        // Highest heading on the Summary page will be h2 as opposed to h1. Set to true if planner is e.g. used in an iframe with an h1 header outside of the planner.
        this.startHeadingsAtH2 = false;
        // Deprecated. Adds balanced margins on all sides of SummaryPage when true. To be used with the summary header and footer. This prop is deprecated - use the Main Header and Main Footer components together with the enableMainMargins prop instead.
        this.enableMargins = false;
        // Adds margins on all sides of SummaryPage when true. To be used with the main header and footer.
        this.enableMainMargins = false;
        // Used as a link for the QR code and in ShareDesignCard. It should be in the same format as the "deeplink" property in DEXF Settings (e.g. "https://{dexf-addon-app}/storageone/pax/web/latest/{ru}/{lc}/#/u/{CODE}").
        this.designLink = null;
        // Used as a link for the QR code in the buy in store sheet.
        this.buyInStoreLink = null;
        // Shows total package weight of the shopping products.
        this.showTotalPackageWeight = false;
        // Shows the assistance card, which informs the user how to get assistance with their design. It has a kiosk state with a different look when the prop kiosk is set to true.
        this.showAssistanceCard = false;
        // Shows the assembly services card, which presents the assembly service availability to the user.
        this.showAssemblyServices = false;
        // The name of the application, e.g. "pax". Used in the booking url in the assistance card.
        this.assistanceCardApplicationName = null;
        // Shows call to action button to give user the possibility to complete their purchase on kiosk. Won't be shown if showAddToBag is true.
        this.showKioskMainCTAButton = false;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        // A list of product item numbers that can be removed in the product list.
        this.productListRemovable = null;
        // Sets the default sorting of the product list.
        this.defaultSortBy = null;
        // Inline messages, displayed below ConfigurationSummary.
        this.messageSection = null;
        // Displays link card when data and Översätta key showLinkCard are set. For image ratio guidelines, see Skapa Card. Hidden on kiosks when kiosk is true.
        this.linkCard = null;
        // State of the component. By using the "stateChange" event combined with this prop it is possible to take over state management. If not set, state is managed internally.
        this.state = null;
        // Shows print button. For example to be used on kiosks with print functionality.
        this.showPrintButton = false;
        // Shows edit design button.
        this.showEditDesignButton = false;
        // A sorted list of content to include in the print layout. When not set, the currently shown tab is printed. To include custom content in the print, the content must also be included as the slot "custom-print-content". E.g. ['productListTab', 'imagesTab', 'securityTab', 'customPrintContent'].
        this.contentToPrint = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // If set, the Summary Page component will hoist itself to this element during print. Should probably be set to `document.body`. Make sure to hide all other UI during print.
        this.printHoistTarget = null;
        // Experimental options. NB: Use at your own discretion. Are considered non-breaking if altered or removed!
        this.experimental = null;
        this.internalState = Object.assign({}, SummaryPageControllerDefaultState);
        this.toastManager = new ToastManager({ theme: this.theme });
        this.listeners = {
            cookieStoreIdChange: this.onCookieStoreIdChange.bind(this),
            cookieZipCodeChange: this.onCookieZipCodeChange.bind(this)
        };
        this.storeIdCookieInformationService = null;
        this.zipCodeCookieInformationService = null;
        this.customTabSlots = [];
        // Used for storing the ZIP code for validation, before writing it to the state.
        this.pendingZipCode = '';
        this.zipIn = ReducerUtility.useReducer(zipInReducer, ZipInDefaultState, this.onZipInStateChange.bind(this), 
        // Using a custom store for the reducer makes the reducer use the controller
        // state (getState/setState) instead of its internal state. Zip in state can
        // therefore be accessed either via `this.zipIn.state` or
        // `this.getState().zipInState`. The former is preferred as it makes it very
        // clear to future developers that the zip in reducer is being used.
        {
            get: () => this.getState().zipInState,
            set: (zipInState) => this.setState({ zipInState })
        });
    }
    async onZipInStateChange(oldState, newState) {
        // If we haven't changed steps, don't bother doing any transitions.
        if (oldState.step === newState.step) {
            return;
        }
        switch (newState.step) {
            case ZipInStepEnum.validation: {
                // ZIP code was submitted. Validate it.
                if (!this.pendingZipCode || this.pendingZipCode.trim().length === 0) {
                    this.zipIn.dispatch({ type: ZipInActionTypeEnum.zipCodeEmpty });
                    return;
                }
                const validationResult = await this.validateZipCode(this.pendingZipCode);
                if (!validationResult?.valid) {
                    this.zipIn.dispatch({ type: ZipInActionTypeEnum.zipCodeInvalid });
                    return;
                }
                const validZipCode = validationResult.content?.formatted ?? validationResult.zipCode;
                this.zipIn.dispatch({
                    type: ZipInActionTypeEnum.zipCodeValid,
                    zipCode: validZipCode
                });
                // This assumes that the only time we validate the zip-code is when the user has made the update in the application.
                this.toastManager.showToast(ZipInToastMessage.getZipCodeToastLabel(this.getState().translations, validZipCode));
                break;
            }
            case ZipInStepEnum.services:
                // ZIP code was valid. Fetch all availabilities.
                try {
                    const assemblyServicesData = await this.fetchAssemblyServices(newState.zipCode);
                    const zipAvailabilities = await this.fetchZipAvailabilities(newState.zipCode);
                    this.zipIn.dispatch({
                        type: ZipInActionTypeEnum.servicesDone,
                        assemblyServicesData,
                        zipAvailabilities
                    });
                }
                catch (error) {
                    this.zipIn.dispatch({
                        type: ZipInActionTypeEnum.zipCodeError
                    });
                    window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
                }
                break;
            case ZipInStepEnum.default:
                this.setState({ visibleModal: null });
                break;
            case ZipInStepEnum.submitted:
                this.setState({ visibleModal: null });
                this.zipCodeCookieInformationService?.setZipCode(newState.zipCode);
                break;
        }
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const state = this.getState();
        return html `
			<kompis-summary-page
				@addToCart="${this.onAddToCart.bind(this)}"
				@addToList="${this.onAddToList.bind(this)}"
				@addUnavailableProductsToListChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({
                addUnavailableProductsToList: event.detail.addUnavailableProductsToList
            });
        }}"
				@addUnavailableToCartAndList="${this.onAddUnavailableToCartAndList.bind(this)}"
				@shareDesign="${this.onShareDesign.bind(this)}"
				@confirmationCardStateChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ confirmationCardState: event.detail.selectedState });
        }}"
				@modalClose="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ visibleModal: null });
            this.zipIn.dispatch({ type: ZipInActionTypeEnum.resetErrors });
        }}"
				@modalOpen="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ visibleModal: event.detail.visibleModal });
        }}}"
				@storeSelectorChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ storeId: event.detail.storeId, visibleModal: null });
            this.storeIdCookieInformationService?.setStoreId(event.detail.storeId);
        }}"
				@sortListChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            // TODO: Should this state really be stored internally in SummaryPageProductList?
            this.setState({ visibleModal: event.detail.visibleModal ?? null });
        }}"
				@resetAddToCartState="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ addToCartState: AddToBagStateEnum.default });
        }}"
				@resetAddToListState="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ addToListState: AddToBagStateEnum.default });
        }}"
				@kioskMainCtaButtonClick="${this.onKioskMainCTAButtonClick.bind(this)}"
				@shareDesignCardStateChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({
                shareDesignCard: {
                    ...this.getState().shareDesignCard,
                    state: event.detail.selectedState
                }
            });
        }}"
				@sendByEmailInput="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@sendByEmailValidation="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            // TODO: Should this state really be handled internally in SummaryPageSheet?
        }}"
				@sendByEmailSend="${this.onSendByEmailSend.bind(this)}"
				@sendByEmailReset="${(event) => {
            // TODO: Should this state really be handled internally in SummaryPageSheet?
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({
                shareDesignCard: {
                    ...this.getState().shareDesignCard,
                    sendByEmailState: SendByEmailStateEnum.default
                }
            });
        }}"
				@saveDesign="${this.onSaveDesignToProfile.bind(this)}"
				@logInAndSaveDesign="${this.onLogInAndSaveDesign.bind(this)}"
				@collectProduct="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ collectedProducts: event.detail.collectedProducts });
        }}"
				@productListRemove="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            const [type, id] = event.detail.itemId?.split('-') || [];
            // We should not update the "shoppingProducts" in the state, as "shoppingItems" and "shoppingProducts" will then diverge.
            // It is up to the planner to update the "shoppingItems" prop, so that it doesn't contain the removed item.
            this.dispatchEvent(new CustomEvent('productListRemove', {
                bubbles: true,
                detail: {
                    itemId: event.detail.itemId,
                    shoppingItems: this.shoppingItems.filter((item) => item.type !== type || item.id !== id)
                }
            }));
        }}"
				@financialServicesContinueButtonClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            // TODO: This could be handled by the modalClose event.
            this.setState({ visibleModal: null });
        }}"
				@linkCardClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.dispatchEvent(new CustomEvent('linkCardClick', {
                bubbles: true,
                detail: event.detail
            }));
        }}"
				@productListExpand="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            // TODO: Should this state really be stored internally in SummaryPageProductList?
        }}"
				@financialServicesMoreInfoClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@selectedImageIndexChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            // TODO: Should this state really be stored internally in SummaryPageImages?
        }}"
				@copyDesign="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@copyDesignCodeClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@copyDesignCodeReset="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@copyDesignLinkClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@copyDesignLinkReset="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@addToCartClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@unavailableProductsModalContinue="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ visibleModal: null });
        }}"
				@tabChange="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.dispatchEvent(new CustomEvent('tabChange', {
                bubbles: true,
                detail: event.detail
            }));
            // TODO: Should this state really be stored internally in SummaryPage?
        }}"
				@printButtonClick="${(event) => {
            this.dispatchEvent(new CustomEvent('printButtonClick', { bubbles: true }));
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				@editDesignButtonClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.dispatchEvent(new CustomEvent('editDesignButtonClick', { bubbles: true }));
        }}"
				@saveZipCode="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.onSaveZipCode(event);
        }}"
				@forgetZipCode="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.onForgetZipCode();
        }}"
				@printQrCodeRendered="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
            this.dispatchEvent(new CustomEvent('printQrCodeRendered', { bubbles: true }));
        }}"
				@openZipIn="${this.onOpenZipIn.bind(this)}"
				@assistanceCardLinkClick="${(event) => {
            this.dispatchTrackAnalyticsEvent(event);
        }}"
				.theme="${this.theme}"
				.translations="${state.translations}"
				.settings="${state.settings}"
				.locale="${this.serviceOptions.locale}"
				.includedProducts="${state.includedProducts}"
				.shoppingProducts="${state.shoppingProducts ?? []}"
				.storeAvailabilities="${state.storeAvailabilities ?? null}"
				.stores="${state.stores ?? []}"
				.storeId="${state.storeId ?? null}"
				.productMeasures="${this.productMeasures}"
				.configurationMeasures="${this.configurationMeasures}"
				.screenshotUrl="${this.screenshotUrl}"
				.screenshotAlt="${this.screenshotAlt}"
				.productName="${this.productName}"
				.productDescription="${this.productDescription}"
				.showShareDesign="${!!this.vpcCode ||
            (!!this.designConfiguration &&
                !!this.designConfiguration.images &&
                !!this.designConfiguration.description)}"
				.showSaveDesign="${this.showSaveDesign &&
            !this.kiosk &&
            state.settings?.kompis.serviceSettings.saveDataSource !== SaveDataSourceEnum.disabled}"
				.showAddToBag="${!this.kiosk}"
				.vpcCode="${this.vpcCode || state.vpcCode}"
				.disableLinks="${this.kiosk}"
				.visibleModal="${state.visibleModal}"
				.addToCartState="${state.addToCartState}"
				.addToListState="${state.addToListState}"
				.shareDesignState="${state.shareDesignState}"
				.kioskMainCTAButtonState="${state.kioskMainCTAButtonState}"
				.confirmationCardState="${state.confirmationCardState}"
				.confirmationCardType="${state.confirmationCardType}"
				.images="${this.images}"
				.securityInformation="${this.securityInformation}"
				.measurements="${this.measurements}"
				.addUnavailableProductsToList="${state.addUnavailableProductsToList}"
				.designLink="${this.getDesignLink()}"
				.shareDesignCard="${{
            ...state.shareDesignCard,
            autocomplete: !this.kiosk,
            showCopyDesignCodeButton: !this.kiosk,
            showCopyLink: !this.kiosk,
            showQrCode: this.kiosk
        }}"
				.buyInStore="${state.buyInStore}"
				.saveDesignToProfile="${state.saveDesignToProfile}"
				.logInAndSaveState="${state.logInAndSaveState}"
				.collectableProducts="${this.collectableProducts}"
				.collectedProducts="${state.collectedProducts}"
				.showDesignText="${this.showDesignText}"
				.productListSections="${this.productListSections}"
				.customTabButtons="${this.customTabButtons}"
				.failedShoppingItems="${state.failedShoppingItems}"
				.startHeadingsAtH2="${this.startHeadingsAtH2}"
				.enableMargins="${this.enableMargins}"
				.enableMainMargins="${this.enableMainMargins}"
				.financingOption="${state.financingOption}"
				.showTotalPackageWeight="${this.showTotalPackageWeight}"
				.showAssistanceCard="${this.showAssistanceCard}"
				.assistanceCardApplicationName="${this.assistanceCardApplicationName}"
				.showKioskMainCTAButton="${this.kiosk && this.showKioskMainCTAButton}"
				.analytics="${this.analytics}"
				.productListRemovable="${this.productListRemovable}"
				.defaultSortBy="${this.defaultSortBy}"
				.messageSection="${this.messageSection}"
				.linkCard="${this.linkCard}"
				.showPrintButton="${this.showPrintButton}"
				.showEditDesignButton="${this.showEditDesignButton}"
				.contentToPrint="${this.contentToPrint}"
				.printHoistTarget="${this.printHoistTarget}"
				.pickingListPrintLayout="${ProductRowLayoutEnum.compactPrint}"
				.showAssemblyServices="${this.showAssemblyServices &&
            state.settings?.kompis.serviceSettings.assemblyServicesDataSource !==
                AssemblyServicesDataSourceEnum.disabled}"
				.zipCode="${this.zipIn.state.zipCode}"
				.zipInCardState="${this.zipIn.state.zipInCardState}"
				.assemblyServices="${{
            assemblyServicesData: this.zipIn.state.assemblyServicesData
        }}"
				.measurementSystem="${this.measurementSystem}"
				.homeDelivery="${{
            zipAvailabilities: this.zipIn.state.zipAvailabilities
        }}"
				.experimental="${this.experimental}"
			>
				${this.customTabSlots}
				<slot slot="custom-print-content" name="custom-print-content"></slot>
			</kompis-summary-page>
		`;
    }
    /**
     * Returns state.
     *
     * @returns State.
     */
    getState() {
        return this.state ?? this.internalState;
    }
    /**
     * Sets state.
     *
     * @param state State.
     * @param [skipRender] Skips rendering.
     * @returns Promise.
     */
    async setState(state, skipRender) {
        const oldState = this.internalState;
        this.internalState = Object.assign({}, this.internalState, state);
        this.dispatchEvent(new CustomEvent('stateChange', {
            bubbles: true,
            detail: { state: this.internalState, oldState: this.state ?? oldState }
        }));
        if (!this.state && !skipRender) {
            await this.requestUpdate();
        }
    }
    connectedCallback() {
        this.customElements.define('kompis-summary-page', SummaryPage);
        super.connectedCallback();
        // State is stored in session storage when redirecting to the login page.
        // Therefore we need to load it back if it exists.
        this.loadStateFromSessionStorage();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.storeIdCookieInformationService?.unsubscribe(this.listeners.cookieStoreIdChange);
        this.storeIdCookieInformationService = null;
        this.zipCodeCookieInformationService?.unsubscribe(this.listeners.cookieZipCodeChange);
        this.zipCodeCookieInformationService = null;
        const oldState = this.internalState;
        this.internalState = Object.assign({}, SummaryPageControllerDefaultState);
        this.dispatchEvent(new CustomEvent('stateChange', {
            bubbles: true,
            detail: { state: this.internalState, oldState: this.state ?? oldState }
        }));
    }
    shouldUpdate(changedProperties) {
        this.fetchChangedProperties(changedProperties);
        if (changedProperties.has('theme')) {
            this.toastManager = new ToastManager({ theme: this.theme });
        }
        if (changedProperties.has('customTabButtons')) {
            const numberOfCustomTabs = SummaryPageCustomTabsUtility.getNumberOfCustomTabsToShow(this.customTabButtons);
            this.customTabSlots = SummaryPageCustomTabsUtility.getCustomTabSlots(numberOfCustomTabs);
        }
        return true;
    }
    async onAddToCart(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.setState({ addToCartState: AddToBagStateEnum.loading });
        const vpcCode = await this.getExistingVpcCodeOrCreateNew();
        let cart = null;
        let error = null;
        try {
            const options = this.getServiceOptionsWithSettings();
            cart = await new ShoppingCartService(options).addToCart({
                items: event.detail.shoppingItems,
                designCode: vpcCode ?? undefined,
                addDesignCodeAsGroup: !!event.detail.completeConfiguration && !!vpcCode
            });
        }
        catch (e) {
            error = e;
        }
        if (error || cart?.errorList.length) {
            const failedShoppingItems = cart
                ? this.getFailedShoppingItemsFromCart(cart)
                : error && error.failedShoppingItems
                    ? error.failedShoppingItems
                    : [];
            if (failedShoppingItems.length) {
                this.setState({
                    failedShoppingItems,
                    addToCartState: AddToBagStateEnum.default,
                    visibleModal: {
                        itemId: null,
                        modal: SummaryPageModalEnum.unavailableProductCard
                    }
                });
            }
            else {
                this.setState({ addToCartState: AddToBagStateEnum.default });
            }
            this.toastManager.showToast(ConfigurationSummaryToastMessage.getAddToCartFail(this.getState().translations));
            if (error) {
                console.error(error);
                window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            }
            return;
        }
        this.setState({
            confirmationCardType: SummaryPageConfirmationCardTypeEnum.addToCartSuccess,
            visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
            addToCartState: AddToBagStateEnum.confirmation
        });
        await this.saveDesignIfLoggedIn();
    }
    async onAddToList(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.setState({ addToListState: AddToBagStateEnum.loading });
        const vpcCode = await this.getExistingVpcCodeOrCreateNew();
        const options = {
            ...this.getServiceOptionsWithSettings(),
            translations: this.getState().translations ?? undefined
        };
        try {
            await new ShoppingListService(options).addToList({
                items: this.getShoppingItems(),
                designCode: vpcCode ?? undefined
            });
        }
        catch (error) {
            this.setState({ addToListState: AddToBagStateEnum.default });
            this.toastManager.showToast(ConfigurationSummaryToastMessage.getAddToListFail(this.getState().translations));
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        this.setState({
            addToListState: AddToBagStateEnum.confirmation,
            confirmationCardType: SummaryPageConfirmationCardTypeEnum.addToListSuccess,
            visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard }
        });
        await this.saveDesignIfLoggedIn();
    }
    async onAddUnavailableToCartAndList(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.setState({
            addToCartState: AddToBagStateEnum.loading,
            addToListState: AddToBagStateEnum.loading
        });
        const vpcCode = await this.getExistingVpcCodeOrCreateNew();
        let cart = null;
        let shoppingList = null;
        const options = {
            ...this.getServiceOptionsWithSettings(),
            translations: this.getState().translations ?? undefined
        };
        try {
            [cart, shoppingList] = await Promise.all([
                new ShoppingCartService(options).addToCart({
                    items: event.detail.shoppingCartItems,
                    designCode: vpcCode ?? undefined
                }),
                new ShoppingListService(options).addToList({
                    items: event.detail.shoppingListItems
                })
            ]);
        }
        catch (error) {
            this.setState({
                addToCartState: AddToBagStateEnum.default,
                addToListState: AddToBagStateEnum.default
            });
            this.toastManager.showToast(ConfigurationSummaryToastMessage.getAddToCartFail(this.getState().translations));
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        const hasErrors = (cartOrList) => {
            return !!cartOrList.errorList.length;
        };
        if (hasErrors(cart) && hasErrors(shoppingList)) {
            this.setState({
                addToCartState: AddToBagStateEnum.default,
                addToListState: AddToBagStateEnum.default
            });
            this.toastManager.showToast(ConfigurationSummaryToastMessage.getAddToCartAndListFail(this.getState().translations));
            return;
        }
        if (hasErrors(cart)) {
            this.setState({
                addToCartState: AddToBagStateEnum.default,
                addToListState: AddToBagStateEnum.default,
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
                confirmationCardType: SummaryPageConfirmationCardTypeEnum.addUnavailabeToCartFailAndListSuccess
            });
        }
        else if (hasErrors(shoppingList)) {
            this.setState({
                addToCartState: AddToBagStateEnum.default,
                addToListState: AddToBagStateEnum.default,
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
                confirmationCardType: SummaryPageConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail
            });
        }
        else {
            this.setState({
                addToCartState: AddToBagStateEnum.confirmation,
                addToListState: AddToBagStateEnum.confirmation,
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
                confirmationCardType: SummaryPageConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess
            });
        }
        await this.saveDesignIfLoggedIn();
    }
    async onShareDesign(event) {
        this.dispatchTrackAnalyticsEvent(event);
        const vpcCode = this.vpcCode || this.getState().vpcCode;
        if (vpcCode) {
            this.setState({
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.shareDesignCard },
                vpcCode,
                shareDesignState: ConfirmationSummaryShareDesignStateEnum.default,
                shareDesignCard: { ...SummaryPageControllerDefaultState.shareDesignCard }
            });
            return;
        }
        if (!this.designConfiguration || !this.designConfiguration.configuration) {
            return;
        }
        this.setState({
            shareDesignState: ConfirmationSummaryShareDesignStateEnum.loading,
            shareDesignCard: { ...SummaryPageControllerDefaultState.shareDesignCard }
        });
        try {
            await this.saveVpcConfigurationAndSetToState();
        }
        catch (error) {
            this.setState({
                confirmationCardType: SummaryPageConfirmationCardTypeEnum.saveVpcFail,
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
                vpcCode: null,
                shareDesignState: ConfirmationSummaryShareDesignStateEnum.default
            });
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        this.setState({
            visibleModal: { itemId: null, modal: SummaryPageModalEnum.shareDesignCard },
            shareDesignState: ConfirmationSummaryShareDesignStateEnum.default
        });
    }
    async onKioskMainCTAButtonClick(event) {
        this.dispatchTrackAnalyticsEvent(event);
        const customEvent = new CustomEvent('kioskMainCtaButtonClick', { bubbles: true });
        this.dispatchEvent(customEvent);
        if (customEvent.defaultPrevented) {
            return;
        }
        const vpcCode = this.vpcCode || this.getState().vpcCode;
        if (vpcCode) {
            this.setState({
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.buyInStore },
                kioskMainCTAButtonState: ConfirmationSummaryKioskMainCTAButtonStateEnum.default,
                buyInStore: { buyInStoreLink: this.getBuyInStoreLink() }
            });
            return;
        }
        this.setState({
            kioskMainCTAButtonState: ConfirmationSummaryKioskMainCTAButtonStateEnum.loading,
            buyInStore: { ...SummaryPageControllerDefaultState.buyInStore }
        });
        try {
            await this.saveVpcConfigurationAndSetToState();
        }
        catch (error) {
            this.setState({
                confirmationCardType: SummaryPageConfirmationCardTypeEnum.saveVpcFail,
                visibleModal: { itemId: null, modal: SummaryPageModalEnum.confirmationCard },
                vpcCode: null,
                kioskMainCTAButtonState: ConfirmationSummaryKioskMainCTAButtonStateEnum.default
            });
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        this.setState({
            kioskMainCTAButtonState: ConfirmationSummaryKioskMainCTAButtonStateEnum.default,
            visibleModal: { itemId: null, modal: SummaryPageModalEnum.buyInStore },
            buyInStore: { buyInStoreLink: this.getBuyInStoreLink() }
        });
    }
    async onSendByEmailSend(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.setState({
            shareDesignCard: {
                ...this.getState().shareDesignCard,
                sendByEmailState: SendByEmailStateEnum.loading
            }
        });
        const vpcCode = this.vpcCode || this.getState().vpcCode;
        if (!vpcCode) {
            throw new Error('VPC code is missing');
        }
        const options = this.getServiceOptionsWithSettings();
        try {
            await new NotificationService(options).sendEmail({
                vpcCode,
                email: event.detail.value,
                linkType: NotificationLinkTypeEnum.deeplink
            });
        }
        catch (error) {
            this.setState({
                shareDesignCard: {
                    ...this.getState().shareDesignCard,
                    sendByEmailState: SendByEmailStateEnum.failed
                }
            });
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        this.setState({
            shareDesignCard: {
                ...this.getState().shareDesignCard,
                sendByEmailState: SendByEmailStateEnum.success
            }
        });
    }
    getServiceOptionsWithSettings() {
        const serviceOptionsWithSettings = this.getState().serviceOptionsWithSettings;
        if (!serviceOptionsWithSettings) {
            throw new Error('Settings must be fetched before using other services.');
        }
        return serviceOptionsWithSettings;
    }
    async onSaveDesignToProfile(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.setState({
            saveDesignToProfile: {
                ...this.getState().saveDesignToProfile,
                saveDesignState: SaveDesignToProfileStateEnum.loading
            }
        });
        let error = null;
        try {
            const user = await this.getAccountService().getUser();
            if (!user) {
                // This shouldn't happen. This check was added for the compiler since getUser returns 'User | null'
                throw new Error('Logged in user was not found.');
            }
            await this.saveDesignToProfile(user);
        }
        catch (e) {
            error = e;
        }
        this.setState({
            visibleModal: null,
            saveDesignToProfile: {
                ...this.getState().saveDesignToProfile,
                saveDesignState: SaveDesignToProfileStateEnum.default
            }
        });
        if (!error) {
            this.toastManager.showToast(SaveDesignToProfileToastMessage.getSaveDesignSuccess(this.getState().translations));
        }
        else {
            this.toastManager.showToast(SaveDesignToProfileToastMessage.getSaveDesignFailure(this.getState().translations));
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
        }
    }
    designIsAlreadySaved() {
        return this.designSaved || this.getState().designSaved;
    }
    async saveDesignIfLoggedIn() {
        if (!this.showSaveDesign || this.designIsAlreadySaved()) {
            return;
        }
        const accountService = this.getAccountService();
        try {
            const user = await accountService.getUser();
            if (user) {
                await this.saveDesignToProfile(user);
                this.toastManager.showToast(SaveDesignToProfileToastMessage.getSaveDesignSuccess(this.getState().translations));
            }
        }
        catch (error) {
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
        }
    }
    async saveDesignToProfile(user) {
        if (!this.designConfiguration) {
            throw new Error('Failed to save design. The prop "designConfiguration" has not been defined.');
        }
        let saveConfiguration;
        if (this.designConfiguration.designId) {
            saveConfiguration = await this.saveNewDesignVersionToProfile(user, this.designConfiguration);
        }
        else {
            saveConfiguration = await this.saveNewDesignToProfile(user, this.designConfiguration);
        }
        if (this.designConfiguration.insightsCombinationFormat &&
            saveConfiguration.icfSaved === false) {
            const error = new Error("Failed to save insights combination format (ICF) when saving design to profile. Service responded 'icfSaved' as false.");
            // Saving ICF is not critical for the end user but we need to inform the planner. This is why we dispatch an error event that will be logged to Sentry (if Sentry is configured), instead of throwing an error.
            window.dispatchEvent(new ErrorEvent('error', {
                message: error.message,
                error
            }));
        }
        this.setState({ designSaved: true });
        const event = new CustomEvent('designSavedToProfile', {
            bubbles: true,
            detail: { designId: saveConfiguration.designId }
        });
        this.dispatchTrackAnalyticsEvent(event);
        this.dispatchEvent(event);
        return saveConfiguration;
    }
    async saveNewDesignToProfile(user, designConfiguration) {
        if (!designConfiguration.images ||
            !designConfiguration.description ||
            !designConfiguration.name ||
            !designConfiguration.versionInfo) {
            throw new Error('Failed to save design. The prop "designConfiguration" is missing mandatory data.');
        }
        const options = this.getServiceOptionsWithSettings();
        return new SaveService(options).saveConfiguration({
            token: user.accessToken,
            configurationOptions: {
                shoppingItems: this.getShoppingItems(),
                configuration: designConfiguration.configuration,
                // Array of images of the configuration as objects consisting of a typeCode and the image as a base64 string.
                configurationImage: designConfiguration.images,
                // The name of the configuration. Currently not shown in any component.
                configurationName: designConfiguration.name,
                // The description of the configuration, used to describe the saved design in other components.
                configurationDescription: designConfiguration.description,
                // Information about the configuration version.
                versionInfo: designConfiguration.versionInfo,
                // ICF is a common limited configuration format enabling the development of analysis tools across applications.
                insightsCombinationFormat: designConfiguration.insightsCombinationFormat ?? undefined
            }
        });
    }
    async saveNewDesignVersionToProfile(user, designConfiguration) {
        if (!designConfiguration.images ||
            !designConfiguration.versionInfo ||
            !designConfiguration.designId) {
            throw new Error('Failed to save design version. The prop "designConfiguration" is missing mandatory data.');
        }
        const options = this.getServiceOptionsWithSettings();
        return new SaveService(options).saveConfiguration({
            token: user.accessToken,
            configurationOptions: {
                shoppingItems: this.getShoppingItems(),
                // The id to save a new version to.
                designId: designConfiguration.designId,
                configuration: designConfiguration.configuration,
                // Array of images of the configuration as objects consisting of a typeCode and the image as a base64 string.
                configurationImage: designConfiguration.images,
                // The name of the configuration. Currently not shown in any component.
                configurationName: designConfiguration.name,
                // The description of the configuration, used to describe the saved design in other components.
                configurationDescription: designConfiguration.description,
                // Information about the configuration version.
                versionInfo: designConfiguration.versionInfo,
                // ICF is a common limited configuration format enabling the development of analysis tools across applications.
                insightsCombinationFormat: designConfiguration.insightsCombinationFormat ?? undefined
            }
        });
    }
    async onLogInAndSaveDesign(event) {
        this.dispatchTrackAnalyticsEvent(event);
        this.dispatchEvent(new CustomEvent('logInAndSaveDesign', { bubbles: true, detail: event.detail }));
        this.setState({
            visibleModal: event?.detail?.visibleModal ?? null
        });
        if (this.loginBehaviour === SummaryPageControllerLoginBehaviourEnum.redirect) {
            await this.logInUsingRedirectAndPrepareSaveDesign();
        }
    }
    async logInUsingRedirectAndPrepareSaveDesign() {
        // The button to log in and save should never appear when already logged in.
        // We can assume that the page will be redirected when attempting to log in.
        this.setState({
            saveDesignOnReload: true,
            logInAndSaveState: ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.loading
        });
        await this.logIn();
    }
    async logIn() {
        if (this.loginBehaviour === SummaryPageControllerLoginBehaviourEnum.disabled) {
            return null;
        }
        const accountService = this.getAccountService();
        const user = await accountService.getUser();
        if (user) {
            // User is already logged in.
            this.setState({
                saveDesignToProfile: {
                    ...this.getState().saveDesignToProfile,
                    loggedIn: true
                }
            });
            return user;
        }
        if (this.loginBehaviour === SummaryPageControllerLoginBehaviourEnum.redirect) {
            this.storeStateInSessionStorage();
        }
        try {
            // As long as redirect is the only supported login method the user should get redirected here.
            await accountService.logIn();
        }
        catch (error) {
            this.clearStateInSessionStorage();
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
        }
        return null;
    }
    async onSaveZipCode(event) {
        this.pendingZipCode = event.detail.zipCode;
        this.zipIn.dispatch({
            type: ZipInActionTypeEnum.submitZipCode
        });
    }
    async onForgetZipCode() {
        this.zipIn.dispatch({
            type: ZipInActionTypeEnum.forgetZipCode
        });
        await this.zipCodeCookieInformationService?.clearZipCode();
    }
    onOpenZipIn() {
        this.setState({
            visibleModal: { itemId: null, modal: SummaryPageModalEnum.zipIn }
        });
    }
    onCookieStoreIdChange(storeId) {
        this.setState({ storeId });
        this.fetchStoreAvailabilities();
    }
    onCookieZipCodeChange(zipCode) {
        if (zipCode === this.zipIn.state.zipCode) {
            return;
        }
        if (zipCode) {
            // We assume that the zip code is valid if it is set in the cookie.
            this.zipIn.dispatch({
                type: ZipInActionTypeEnum.zipCodeValid,
                zipCode
            });
        }
        else {
            this.zipIn.dispatch({
                type: ZipInActionTypeEnum.forgetZipCode
            });
        }
    }
    async fetchChangedProperties(changedProperties) {
        if (!this.vpcCode &&
            this.getState().vpcCode !== null &&
            (changedProperties.has('shoppingItems') ||
                changedProperties.has('designConfiguration') ||
                changedProperties.has('serviceOptions'))) {
            this.setState({ vpcCode: null }, true);
        }
        // Service options wont change during the component lifetime. But it might be null when it's initialized.
        if (changedProperties.has('serviceOptions')) {
            await this.fetchDexfSettings();
            this.subscribeToCookieInformation();
            await Promise.all([
                this.fetchPhraseTranslations(),
                this.fetchShoppingProducts(),
                this.fetchStores(),
                this.fetchStoreId(),
                this.fetchZipCode(),
                this.fetchUserLoggedInState()
            ]);
            await Promise.all([this.fetchFinancingOption(), this.fetchStoreAvailabilities()]);
            if (!this.vpcCode && (this.showAssistanceCard || this.designLink)) {
                await this.fetchVpcCode();
            }
            if (this.getState().saveDesignOnReload) {
                await this.saveDesignAfterPageReload();
            }
            this.sendComponentLoadedEvent();
        }
        else {
            if (!this.getState().serviceOptionsWithSettings) {
                await this.fetchDexfSettings();
            }
            if (changedProperties.has('shoppingItems')) {
                await this.fetchShoppingProducts();
                // This will trigger a new fetch of home delivery and assembly services.
                this.zipIn.dispatch({
                    type: ZipInActionTypeEnum.zipCodeValid,
                    zipCode: this.zipIn.state.zipCode
                });
                await Promise.all([this.fetchFinancingOption(), this.fetchStoreAvailabilities()]);
            }
            if (!this.vpcCode &&
                (changedProperties.has('showAssistanceCard') || changedProperties.has('designLink')) &&
                (this.showAssistanceCard || this.designLink)) {
                await this.fetchVpcCode();
            }
            this.sendComponentLoadedEvent();
        }
    }
    async saveDesignAfterPageReload() {
        const accountService = this.getAccountService();
        try {
            const user = await accountService.getUser();
            if (user) {
                await this.saveDesignToProfile(user);
                this.toastManager.showToast(SaveDesignToProfileToastMessage.getSaveDesignSuccess(this.getState().translations));
                this.setState({
                    logInAndSaveState: ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.default,
                    saveDesignOnReload: false
                });
                return;
            }
        }
        catch (error) {
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
        }
        this.toastManager.showToast(SaveDesignToProfileToastMessage.getSaveDesignFailure(this.getState().translations));
        this.setState({
            logInAndSaveState: ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.failed,
            saveDesignOnReload: false
        });
    }
    async fetchPhraseTranslations() {
        const translations = await new TranslationsService(this.serviceOptions).getTranslations();
        this.setState({ translations: translations.kompis });
    }
    async fetchDexfSettings() {
        const settings = await new SettingsService(this.serviceOptions).getApplicationSettings(this.serviceOptions.filterSegment);
        this.setState({
            settings,
            serviceOptionsWithSettings: {
                ...this.serviceOptions,
                settings
            }
        });
    }
    async fetchShoppingProducts() {
        const options = this.getServiceOptionsWithSettings();
        const service = new ProductService(options);
        const shoppingProducts = await service.getShoppingProducts(this.shoppingItems, KompisDefaultFields);
        const includedItems = [];
        for (const shoppingProduct of shoppingProducts) {
            const content = shoppingProduct.product.content;
            if (content?.itemType === 'SPR' && content.child) {
                for (const child of content.child) {
                    includedItems.push({
                        type: 'ART',
                        id: child.itemId.split('-')[1]
                    });
                }
            }
        }
        if (!includedItems.length) {
            this.setState({ shoppingProducts });
            return;
        }
        const includedProducts = await service.getProducts(includedItems, KompisDefaultFields);
        this.setState({ shoppingProducts, includedProducts });
    }
    async fetchUserLoggedInState() {
        if (!this.showSaveDesign || this.getState().saveDesignToProfile.loggedIn) {
            return;
        }
        const accountService = this.getAccountService();
        let user = null;
        try {
            user = await accountService.getUser({ useSilentAuthentication: true });
        }
        catch (error) {
            console.error(error);
        }
        if (user) {
            this.setState({
                saveDesignToProfile: {
                    ...this.getState().saveDesignToProfile,
                    loggedIn: true
                }
            });
        }
    }
    async fetchStoreAvailabilities() {
        const options = this.getServiceOptionsWithSettings();
        const storeId = this.getState().storeId;
        if (!storeId ||
            options.settings.kompis.serviceSettings.storeAvailabilityDataSource ===
                StoreAvailabilityDataSourceEnum.disabled) {
            this.setState({ storeAvailabilities: null });
            return;
        }
        let storeAvailabilities = null;
        try {
            storeAvailabilities = await new CheckoutAvailabilityService(options).getStoreAvailabilities(storeId, this.getShoppingItems());
        }
        catch (error) {
            this.setState({ storeAvailabilities: null });
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return;
        }
        this.setState({ storeAvailabilities });
    }
    async validateZipCode(zipCode) {
        const options = this.getServiceOptionsWithSettings();
        try {
            return await new ZipValidationService(options).getZipValidation(zipCode);
        }
        catch (error) {
            console.error(error);
            window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            return null;
        }
    }
    async fetchZipAvailabilities(zipCode) {
        const options = this.getServiceOptionsWithSettings();
        if (options.settings.kompis.serviceSettings.zipAvailabilityDataSource ===
            ZipAvailabilityDataSourceEnum.disabled) {
            return null;
        }
        return await new CheckoutAvailabilityService(options).getZipAvailabilities(zipCode, this.getShoppingItems());
    }
    async fetchAssemblyServices(zipCode) {
        const options = this.getServiceOptionsWithSettings();
        if (options.settings.kompis.serviceSettings.assemblyServicesDataSource ===
            AssemblyServicesDataSourceEnum.disabled ||
            this.showAssemblyServices === false) {
            return null;
        }
        return await new AssemblyServicesService(options).getAssemblyServices(zipCode, this.getShoppingItems());
    }
    async fetchStores() {
        const options = this.getServiceOptionsWithSettings();
        const stores = await new StoreService(options).getStores();
        this.setState({ stores });
    }
    async fetchFinancingOption() {
        const shoppingProducts = this.getState().shoppingProducts;
        if (!shoppingProducts || this.experimental?.hideFinancialServices) {
            return;
        }
        const options = this.getServiceOptionsWithSettings();
        const financingOption = await new FinancingService(options).getFinancingOption(shoppingProducts);
        this.setState({ financingOption });
    }
    async fetchStoreId() {
        const storeId = (await this.storeIdCookieInformationService?.getStoreId()) ?? null;
        this.setState({ storeId });
    }
    async fetchZipCode() {
        const zipCode = (await this.zipCodeCookieInformationService?.getZipCode()) ?? null;
        this.onCookieZipCodeChange(zipCode);
    }
    async fetchVpcCode() {
        if (this.vpcCode || this.getState().vpcCode) {
            return;
        }
        await this.saveVpcConfigurationAndSetToState();
    }
    async getExistingVpcCodeOrCreateNew() {
        const vpcCode = this.vpcCode ?? this.getState().vpcCode;
        if (vpcCode) {
            return vpcCode;
        }
        else {
            try {
                const vpcConfiguration = await this.saveVpcConfigurationAndSetToState();
                return vpcConfiguration?.configurationId ?? null;
            }
            catch (error) {
                console.error(error);
                window.dispatchEvent(new ErrorEvent('error', { error, message: error.message }));
            }
        }
        return null;
    }
    async saveVpcConfigurationAndSetToState() {
        const vpcConfiguration = await this.saveVpcConfiguration();
        if (vpcConfiguration) {
            this.setState({ vpcCode: vpcConfiguration.configurationId });
        }
        return vpcConfiguration;
    }
    async saveVpcConfiguration() {
        if (!this.designConfiguration) {
            return null;
        }
        const options = this.getServiceOptionsWithSettings();
        const response = await new VpcService(options).saveConfiguration(this.designConfiguration.configuration, this.getShoppingItems(), this.designConfiguration.images ?? undefined, this.designConfiguration.insightsCombinationFormat ?? undefined);
        if (this.designConfiguration.insightsCombinationFormat && response?.icfSaved === false) {
            const error = new Error("Failed to save insights combination format (ICF) when saving VPC. Service responded 'icfSaved' as false.");
            // Saving ICF is not critical for the end user but we need to inform the planner. This is why we dispatch an error event that will be logged to Sentry (if Sentry is configured), instead of throwing an error.
            window.dispatchEvent(new ErrorEvent('error', {
                message: error.message,
                error
            }));
        }
        return response;
    }
    subscribeToCookieInformation() {
        const options = this.getServiceOptionsWithSettings();
        if (this.storeIdCookieInformationService) {
            this.storeIdCookieInformationService.unsubscribe(this.listeners.cookieStoreIdChange);
        }
        if (this.zipCodeCookieInformationService) {
            this.zipCodeCookieInformationService.unsubscribe(this.listeners.cookieZipCodeChange);
        }
        this.storeIdCookieInformationService = new StoreIdCookieInformationService(options);
        this.storeIdCookieInformationService.subscribe(this.listeners.cookieStoreIdChange);
        this.zipCodeCookieInformationService = new ZipCodeCookieInformationService(options);
        this.zipCodeCookieInformationService.subscribe(this.listeners.cookieZipCodeChange);
    }
    getFailedShoppingItemsFromCart(cart) {
        const shoppingItems = [];
        if (!cart.errorList?.length) {
            return shoppingItems;
        }
        for (const error of cart.errorList) {
            for (const itemNo of error.items) {
                shoppingItems.push({
                    itemNo,
                    message: error.message
                });
            }
        }
        return shoppingItems;
    }
    getShoppingItems() {
        return ProductUtility.getShoppingItems(this.getState().shoppingProducts || []);
    }
    sendComponentLoadedEvent() {
        if (!this.getState().componentLoadedEventSent) {
            const event = new CustomEvent('summaryLoaded', { bubbles: true });
            this.dispatchTrackAnalyticsEvent(event);
            this.setState({ componentLoadedEventSent: true });
        }
    }
    dispatchTrackAnalyticsEvent(event) {
        this.dispatchEvent(new CustomEvent('trackAnalytics', {
            bubbles: true,
            detail: { event: event.type, data: event.detail }
        }));
    }
    storeStateInSessionStorage() {
        // eslint-disable-next-line no-console
        console.log('SummaryPageController: Storing state in session storage.');
        // We should not store fetchable data as it may reach the limit of the session storage.
        // Therefore only store the state that is needed to restore the page.
        const state = {
            ...this.getState(),
            homeDelivery: {
                ...this.getState().homeDelivery,
                zipAvailabilities: []
            }
        };
        delete state.translations;
        delete state.settings;
        delete state.serviceOptionsWithSettings;
        delete state.shoppingProducts;
        delete state.includedProducts;
        delete state.storeAvailabilities;
        delete state.addToCartState;
        delete state.addToListState;
        delete state.shareDesignState;
        delete state.buyInStore;
        delete state.confirmationCardState;
        delete state.financingOption;
        sessionStorage.setItem('kompis-summary-page-controller-state', JSON.stringify(state));
    }
    loadStateFromSessionStorage() {
        const state = sessionStorage.getItem('kompis-summary-page-controller-state');
        if (state) {
            // eslint-disable-next-line no-console
            console.log('SummaryPageController: Restoring state from session storage.');
            sessionStorage.removeItem('kompis-summary-page-controller-state');
            this.setState(JSON.parse(state));
        }
    }
    clearStateInSessionStorage() {
        // eslint-disable-next-line no-console
        console.log('SummaryPageController: Clearing state in session storage.');
        sessionStorage.removeItem('kompis-summary-page-controller-state');
    }
    substitutePlannerLink(link) {
        const vpcCode = this.vpcCode || this.getState().vpcCode;
        if (!vpcCode) {
            return null;
        }
        const [language, country] = this.serviceOptions.locale.split('-');
        return link
            .replace('{dexf-addon-app}', 'www.ikea.com/addon-app/')
            .replace('{ru}', language)
            .replace('{lc}', country.toLowerCase())
            .replace('{CODE}', vpcCode ?? '');
    }
    getDesignLink() {
        if (!this.designLink) {
            return null;
        }
        return this.substitutePlannerLink(this.designLink);
    }
    getBuyInStoreLink() {
        if (!this.buyInStoreLink) {
            return null;
        }
        return this.substitutePlannerLink(this.buyInStoreLink);
    }
    getAccountService() {
        const options = this.getServiceOptionsWithSettings();
        return new AccountService({
            ...options,
            // Use same segment for login as for DEXF settings.
            settingsSegment: options.filterSegment
        });
    }
}
__decorate([
    property({ type: Object, required: true })
], SummaryPageController.prototype, "serviceOptions", void 0);
__decorate([
    property({ type: Array, required: true })
], SummaryPageController.prototype, "shoppingItems", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "designConfiguration", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "loginBehaviour", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "productMeasures", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "configurationMeasures", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "productName", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "productDescription", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showSaveDesign", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "vpcCode", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "designSaved", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "kiosk", void 0);
__decorate([
    property({ type: Array })
], SummaryPageController.prototype, "images", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "securityInformation", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "measurements", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "collectableProducts", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showDesignText", void 0);
__decorate([
    property({ type: Array })
], SummaryPageController.prototype, "productListSections", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "customTabButtons", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "startHeadingsAtH2", void 0);
__decorate([
    property({ type: Boolean })
    /**
     * @deprecated Use the Main Header and Main Footer components together with the enableMainMargins prop instead.
     */
], SummaryPageController.prototype, "enableMargins", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "enableMainMargins", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "designLink", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "buyInStoreLink", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showTotalPackageWeight", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showAssistanceCard", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showAssemblyServices", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "assistanceCardApplicationName", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showKioskMainCTAButton", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "analytics", void 0);
__decorate([
    property({ type: Array })
], SummaryPageController.prototype, "productListRemovable", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "defaultSortBy", void 0);
__decorate([
    property({ type: Array })
], SummaryPageController.prototype, "messageSection", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "linkCard", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "state", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showPrintButton", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPageController.prototype, "showEditDesignButton", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "contentToPrint", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "measurementSystem", void 0);
__decorate([
    property({ type: String })
], SummaryPageController.prototype, "printHoistTarget", void 0);
__decorate([
    property({ type: Object })
], SummaryPageController.prototype, "experimental", void 0);
//# sourceMappingURL=SummaryPageController.js.map